import { Component, ComponentType, ErrorInfo, FC, ReactNode, Suspense } from 'react';
import { lazyWithCustomError } from 'src/utils/lazyWithCustomError';

type BoundaryProps = { children: ReactNode };
type BoundaryState = { failed: boolean };

class ChunkBoundary extends Component<BoundaryProps, BoundaryState> {
  public state: BoundaryState = { failed: false };

  public static getDerivedStateFromError(error: Error): BoundaryState {
    if (isChunkLoadError(error)) return { failed: true };
    throw error;
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    console.warn('[lazyWidget] widget chunk failed to load, skipping it', error, info);
  }

  public render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

const isChunkLoadError = (error: Error): boolean =>
  error?.name === 'ChunkLoadError' || typeof (error as { additionalMessage?: string })?.additionalMessage === 'string';

export const lazyWidget = <P extends object>(factory: () => Promise<{ default: ComponentType<P> }>): FC<P> => {
  const Lazy: ComponentType<any> = lazyWithCustomError(factory);

  return (props: P) => (
    <ChunkBoundary>
      <Suspense fallback={null}>
        <Lazy {...props} />
      </Suspense>
    </ChunkBoundary>
  );
};
